import { isServer, keepPreviousData, QueryClient } from '@tanstack/react-query';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { routeTree } from './routeTree.gen';
import {
	persistQueryClient,
	PersistQueryClientProvider,
	removeOldestQuery,
} from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { ReactNode } from 'react';
import { gtag } from '@worldcoo/ui/lib/analytics';
import { loadLandingTranslation } from './lib/i18n';
import { getRequestContext } from './lib/server-functions';
import { DefaultCatchBoundary } from './routes/-components/default-catch-boundary';
import { NotFound } from './routes/-components/not-found';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';

/**
 * TODO: We're using the tanstack start built-in RPC instead of trpc because trpc
 * needs to send the proper headers to the backend and this disables tanstack query persist
 * We lose the batching from TRPC but we gain the ability to persist queries
 */
export async function getRouter() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				retry: false,
				staleTime: 60 * 1000,
				gcTime: Infinity,
				placeholderData: keepPreviousData,
			},
		},
	});

	const persister = createAsyncStoragePersister({
		storage: typeof window !== 'undefined' ? window.localStorage : undefined,
		retry: removeOldestQuery,
	});

	const buster = 'v3';

	// TODO: We're calling it also here instead of relying only on <PersistQueryClientProvider />
	// because on quick reload is still not restoring from persister
	if (!isServer) {
		persistQueryClient({ queryClient, persister, buster });
	}

	const { locale } = await getRequestContext();
	await loadLandingTranslation(locale);

	const router = createTanStackRouter({
		routeTree,
		context: { queryClient },
		defaultPreload: 'intent',
		scrollRestoration: true,
		rewrite: {
			input: ({ url }) => {
				// Check if it's the main domain (no subdomain or www)
				const parts = url.hostname.split('.');
				if (
					parts[0] === 'www' ||
					(parts.length < 3 && parts.at(-1) !== 'localhost') ||
					(parts.length < 2 && parts.at(-1) === 'localhost')
				) {
					// Rewrite to /www/...
					url.pathname = `/www${url.pathname}`;
				} else {
					// Extract portalId from subdomain (e.g., eroski.example.com -> eroski)
					const parts = url.hostname.split('.');
					const portalId = parts[0];
					url.pathname = `/${portalId}${url.pathname}`;
				}
				return url;
			},
			output: ({ url }) => {
				// Remove the portalId prefix from the pathname for clean URLs
				const parts = url.pathname.split('/').filter(Boolean);
				if (parts.length > 0) {
					if (parts[0] === 'www') {
						// Remove /www prefix
						url.pathname = '/' + parts.slice(1).join('/') || '/';
					} else {
						// Remove portalId prefix (it's already in the subdomain)
						url.pathname = '/' + parts.slice(1).join('/') || '/';
					}
				}
				return url;
			},
		},
		// Since we're using React Query, we don't want loader calls to ever be stale
		// This will ensure that the loader is always called when the route is preloaded or visited
		defaultPreloadStaleTime: 0,
		Wrap: (props: { children: ReactNode }) => {
			return (
				<PersistQueryClientProvider client={queryClient} persistOptions={{ persister, buster }}>
					{props.children}
				</PersistQueryClientProvider>
			);
		},
		defaultErrorComponent: (props) => (
			<I18nProvider i18n={i18n}>
				<DefaultCatchBoundary {...props} />
			</I18nProvider>
		),
		defaultNotFoundComponent: () => <NotFound />,
	});

	setupRouterSsrQueryIntegration({
		router,
		queryClient,
		wrapQueryClient: false,
	});

	/**
	 * Setup google analytics
	 */
	if (!isServer) {
		gtag('js', new Date());
		gtag('config', import.meta.env.VITE_ANALYTICS_CODE);

		router.history.subscribe((url) => {
			gtag('event', 'page_view', { page_location: url.location.href });
		});

		/**
		 * Reload the page if it fails to load dynamic imports
		 * When a new deployment occurs, the hosting service may delete the assets from previous deployments.
		 * As a result, a user who visited your site before the new deployment might encounter an import error.
		 * This error happens because the assets running on that user's device are outdated and it tries to import
		 * the corresponding old chunk, which is deleted.
		 * This event is useful for addressing this situation. In this case, make sure to set Cache-Control: no-cache on the HTML file,
		 * otherwise the old assets will be still referenced.
		 * See: https://vite.dev/guide/build#load-error-handling
		 */
		window.addEventListener('vite:preloadError', () => {
			window.location.reload();
		});
	}

	return router;
}
